import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Swords, Clock, Settings, ChevronDown, ChevronRight, Trophy, Flag, CheckCircle2, MinusCircle, CalendarClock, Info } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useEventStream } from "../lib/sse";
import { Page, Loader, Empty, GhostButton } from "../components/UI";
import { MatchStatusBadge, MatchResultCard, LiveScoreHeader, ClinchBanner, MatchTimeline, ExhibitionBadge } from "../components/Arena";
import { ClubCrest } from "../components/ClubCrest";
import { ShareButton } from "../components/ShareButton";

const TABS = ["Overview", "Duels", "Timeline"];
function teamLink(c) { return c?.is_national && c?.country_iso3 ? `/nations/${c.country_iso3}` : `/club/${c?.id}`; }
function fmt(iso) { try { return new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } }

export default function MatchPage() {
  const { matchId } = useParams();
  const { user, hasRole } = useAuth();
  const [match, setMatch] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [tab, setTab] = useState("Overview");
  const busy = useRef(false);

  const load = useCallback(() => {
    if (busy.current) return;
    busy.current = true;
    api.get(`/matches/${matchId}`)
      .then(({ data }) => setMatch(data))
      .catch(() => setNotFound(true))
      .finally(() => { busy.current = false; });
  }, [matchId]);

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [load]);

  useEventStream(match?.id ? `/matches/${match.id}/stream` : null, () => load(), !!match?.id);

  if (notFound) return <Page><Empty label="Match not found" /></Page>;
  if (!match) return <Loader label="Loading match center…" />;

  const isFinal = match.status === "FINAL";
  const resolved = match.live?.resolved_count ?? 0;
  const inPlay = !isFinal && (resolved > 0 ||
    ["SUBMISSION_CLOSED", "EVENTS_IN_PROGRESS", "SETTLEMENT_IN_PROGRESS", "REGULATION_COMPLETE"].includes(match.status));
  const upcoming = !isFinal && !inPlay;
  const openOngoing = ["SUBMISSION_OPEN", "SUBMISSION_IN_PROGRESS"].includes(match.status);
  const hp = match.home_team_points, ap = match.away_team_points;
  const homeWin = match.match_result?.result_type === "HOME_WIN";
  const awayWin = match.match_result?.result_type === "AWAY_WIN";
  const timelineCount = (match.timeline || []).length;

  return (
    <div className="animate-fade-up pb-24 overflow-x-hidden">
      {/* ---- MATCH CENTER HEADER ---- */}
      <section className="relative overflow-hidden border-b border-[rgba(212,175,55,0.22)] bg-[#000000]">
        <div className="absolute inset-0 mesh-grid opacity-[0.14] pointer-events-none" />
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(60% 130% at 50% -20%, rgba(212,175,55,0.18) 0%, transparent 58%)" }} />
        <div className="mx-auto max-w-[1500px] px-4 sm:px-8 lg:px-12 relative py-7 sm:py-9">
          <Link to="/matches" className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-[#E5B842] mb-5" style={{ transition: "color 0.15s ease" }}>
            <ArrowLeft className="w-4 h-4" /> All matches
          </Link>
          <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
            <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-wider text-slate-400 min-w-0">
              <span className="text-[#E5B842] truncate">{match.public_match_id}</span>
              {(match.matchday || match.round) && <><span className="text-slate-700">·</span><span>{match.matchday ? `Matchday ${match.matchday}` : `Round ${match.round}`}</span></>}
              {match.context_label && <><span className="text-slate-700">·</span><span>{match.context_label}</span></>}
            </div>
            <div className="flex items-center gap-2">
              {match.is_exhibition && <ExhibitionBadge />}
              <MatchStatusBadge status={match.status} />
              {isFinal && <ShareButton kind="match" id={match.id} testid="share-match-btn" />}
            </div>
          </div>

          <h1 className="sr-only">{match.home_club?.name} versus {match.away_club?.name} — WSTF Match Center</h1>
          <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 sm:gap-8">
            <TeamHead club={match.home_club} win={homeWin} align="right" />
            <div className="flex flex-col items-center shrink-0">
              {isFinal || inPlay ? (
                <>
                  <div className="font-heading text-4xl sm:text-6xl font-black tabular-nums whitespace-nowrap">
                    <span className={homeWin ? "text-[#E5B842]" : "text-slate-50"}>{hp ?? 0}</span>
                    <span className="text-slate-600 mx-2">–</span>
                    <span className={awayWin ? "text-[#E5B842]" : "text-slate-50"}>{ap ?? 0}</span>
                  </div>
                  <span className="text-[9px] font-mono uppercase tracking-[0.2em] text-slate-500 mt-1">Team Points</span>
                </>
              ) : (
                <div className="flex flex-col items-center text-slate-400">
                  <Swords className="w-7 h-7" />
                  <span className="text-[10px] font-mono mt-1">{fmt(match.scheduled_date || match.submission_open)}</span>
                </div>
              )}
            </div>
            <TeamHead club={match.away_club} win={awayWin} align="left" />
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-[1500px] px-4 sm:px-8 lg:px-12 pt-6">
        {/* ---- TABS ---- */}
        <div className="flex gap-1 border-b border-[#2b2512] mb-7 overflow-x-auto no-scrollbar" data-testid="match-tabs">
          {TABS.map((t) => (
            <button key={t} data-testid={`match-tab-${t.toLowerCase()}`} onClick={() => setTab(t)}
              className={`px-4 py-2.5 text-sm font-heading font-bold uppercase tracking-wide border-b-2 whitespace-nowrap inline-flex items-center gap-1.5 ${tab === t ? "border-[#E5B842] text-[#E5B842]" : "border-transparent text-slate-400 hover:text-slate-200"}`}
              style={{ transition: "color 0.15s ease" }}>
              {t}{t === "Duels" && <span className="text-[10px] font-mono text-slate-600">{match.duels?.length || 0}</span>}
              {t === "Timeline" && timelineCount > 0 && <span className="text-[10px] font-mono text-slate-600">{timelineCount}</span>}
            </button>
          ))}
        </div>

        {/* ---- OVERVIEW ---- */}
        {tab === "Overview" && (
          <div data-testid="match-overview" className="space-y-6">
            {isFinal && <MatchResultCard match={match} />}
            {inPlay && <><ClinchBanner match={match} /><LiveScoreHeader match={match} /></>}
            {upcoming && (
              <div data-testid="match-upcoming" className="rounded-2xl border border-[#2b2512] bg-[#0f0f0f] p-6">
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[#E5B842] font-mono font-bold mb-3"><CalendarClock className="w-4 h-4" /> Upcoming Match</div>
                <div className="grid sm:grid-cols-3 gap-4 font-mono text-sm">
                  <Meta label="Scheduled" value={fmt(match.scheduled_date || match.submission_open)} />
                  <Meta label="Submission window" value={`${fmt(match.submission_open)} → ${fmt(match.submission_close)}`} />
                  <Meta label="Coupons submitted" value={`${match.home_coupon_count}/${match.coupons_per_team} · ${match.away_coupon_count}/${match.coupons_per_team}`} />
                </div>
                <p className="mt-4 text-xs text-slate-500 flex items-start gap-2"><Info className="w-3.5 h-3.5 shrink-0 mt-0.5 text-[#E5B842]" /><span>Each club submits {match.coupons_per_team} blind ArenaBook coupons; they are paired and revealed simultaneously to settle Team Points. No result yet.</span></p>
              </div>
            )}

            {/* metadata + rivalry */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="match-metadata">
              <Meta label="Competition" value={match.context_label || (match.is_national_fixture ? "National Team" : match.is_cup_fixture ? "Cup" : match.is_continental_fixture ? "Continental" : "League")} />
              <Meta label={match.matchday ? "Matchday" : "Round"} value={match.matchday || match.round || "—"} />
              <Meta label="Coupons / team" value={match.coupons_per_team} />
              <Meta label="Scheduled" value={fmt(match.scheduled_date || match.submission_open)} />
            </div>

            <div className="flex flex-wrap gap-2">
              {match.rivalry && (
                <Link to={match.rivalry.link} data-testid="match-rivalry-link">
                  <GhostButton><Swords className="w-4 h-4" /> View Rivalry</GhostButton>
                </Link>
              )}
              {openOngoing && user && (
                <Link to={`/match/${match.id}/play`}><GhostButton data-testid="match-play-btn">Create ArenaBook coupon</GhostButton></Link>
              )}
              {hasRole("WSBF_ADMIN", "FEDERATION_ADMIN") && (
                <Link to={`/match/${match.id}/control`}><GhostButton data-testid="match-control-btn"><Settings className="w-4 h-4" /> Match control</GhostButton></Link>
              )}
            </div>
          </div>
        )}

        {/* ---- DUELS ---- */}
        {tab === "Duels" && <DuelsTab match={match} />}

        {/* ---- TIMELINE ---- */}
        {tab === "Timeline" && (
          <div data-testid="match-timeline-tab" className="max-w-3xl">
            {timelineCount === 0
              ? <Empty label="No timeline events recorded for this match." />
              : <MatchTimeline timeline={match.timeline} />}
          </div>
        )}
      </div>
    </div>
  );
}

function TeamHead({ club, win, align }) {
  return (
    <Link to={teamLink(club)} className={`flex items-center gap-3 min-w-0 ${align === "right" ? "flex-row-reverse text-right justify-end" : "justify-start"}`}>
      <ClubCrest club={club} size={56} />
      <div className="min-w-0">
        <div className={`font-heading text-base sm:text-xl font-black uppercase tracking-tight truncate ${win ? "text-[#E5B842]" : "text-slate-50"}`}>{club?.name}</div>
        <div className="text-[10px] font-mono text-slate-500 flex items-center gap-1 justify-inherit">
          {club?.is_national ? <Flag className="w-3 h-3" /> : null}{club?.short_name}
        </div>
      </div>
    </Link>
  );
}

function Meta({ label, value }) {
  return (
    <div className="rounded-xl border border-[#2b2512] bg-[#0f0f0f] p-4">
      <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-mono mb-1">{label}</div>
      <div className="font-heading text-lg font-bold text-slate-50 truncate">{String(value)}</div>
    </div>
  );
}

/* ---------------- Duels tab: compact scannable rows + lazy expand ---------------- */
const DUEL_FILTERS = [["all", "All"], ["home", "Home wins"], ["away", "Away wins"], ["split", "Split"]];

function DuelsTab({ match }) {
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(null);
  const regulation = (match.duels || []).filter((d) => !d.is_extra);
  const extra = (match.duels || []).filter((d) => d.is_extra);

  const match3 = (d) => filter === "all"
    || (filter === "home" && d.winner_side === "HOME" && !d.split)
    || (filter === "away" && d.winner_side === "AWAY" && !d.split)
    || (filter === "split" && d.split);
  const shown = regulation.filter(match3);

  return (
    <div data-testid="duels-tab">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className="font-heading text-2xl font-extrabold uppercase tracking-tight text-slate-50">Regulation Duels <span className="text-[#E5B842]">— {regulation.length}</span></h2>
        <div className="inline-flex gap-1 p-1 rounded-xl bg-[#0a0a0a] border border-[#2b2512]">
          {DUEL_FILTERS.map(([k, l]) => (
            <button key={k} data-testid={`duel-filter-${k}`} onClick={() => setFilter(k)}
              className={`px-3 py-1.5 rounded-lg text-xs font-heading font-bold uppercase ${filter === k ? "bg-[#E5B842] text-[#000000]" : "text-slate-300 hover:text-[#E5B842]"}`}
              style={{ transition: "background-color 0.15s ease, color 0.15s ease" }}>{l}</button>
          ))}
        </div>
      </div>

      <p className="text-[11px] font-mono text-slate-500 mb-4">Odds shown are coupon/selection total odds — never a match score. The winner comes from settlement; higher odds set the Team Point value, not the result.</p>

      <div className="space-y-2">
        {shown.map((d) => <DuelLine key={d.sequence_number} d={d} home={match.home_club} away={match.away_club} open={open === d.sequence_number} onToggle={() => setOpen(open === d.sequence_number ? null : d.sequence_number)} />)}
        {shown.length === 0 && <Empty label="No duels match this filter" />}
      </div>

      {extra.length > 0 && (
        <div className="mt-8">
          <h2 className="font-heading text-2xl font-extrabold uppercase tracking-tight text-slate-50 mb-4">Extra Duels <span className="text-fuchsia-400">— {extra.length}</span></h2>
          <div className="space-y-2">
            {extra.map((d) => <DuelLine key={`x${d.sequence_number}`} d={d} home={match.home_club} away={match.away_club} open={open === `x${d.sequence_number}`} onToggle={() => setOpen(open === `x${d.sequence_number}` ? null : `x${d.sequence_number}`)} extra />)}
          </div>
        </div>
      )}
    </div>
  );
}

function Outcome({ d }) {
  if (d.status !== "RESOLVED") {
    return <span className="inline-flex items-center gap-1 text-[11px] font-mono uppercase text-slate-500"><Clock className="w-3.5 h-3.5" /> {d.status.replace(/_/g, " ")}</span>;
  }
  if (d.split) return <span className="inline-flex items-center gap-1 text-[11px] font-mono font-bold uppercase text-slate-400"><MinusCircle className="w-3.5 h-3.5" /> Split · 0 pts</span>;
  const side = d.winner_side === "HOME" ? "Home win" : "Away win";
  return <span className="inline-flex items-center gap-1 text-[11px] font-mono font-bold uppercase text-emerald-400"><CheckCircle2 className="w-3.5 h-3.5" /> {side} · +{d.team_points} TP</span>;
}

function DuelLine({ d, home, away, open, onToggle, extra }) {
  const hw = d.winner_side === "HOME" && !d.split;
  const aw = d.winner_side === "AWAY" && !d.split;
  return (
    <div data-testid={`duel-slot-${d.sequence_number}`} className={`rounded-xl border bg-[#0f0f0f] overflow-hidden ${extra ? "border-fuchsia-500/30" : "border-[#2b2512]"}`}>
      <button onClick={onToggle} className="w-full grid grid-cols-[auto_1fr_auto] md:grid-cols-[48px_1fr_auto_1fr_150px_auto] items-center gap-x-3 gap-y-1 px-4 py-3 text-left hover:bg-[#161616]/40" style={{ transition: "background-color 0.15s ease" }}>
        <span className="font-heading font-black text-[#E5B842] text-sm">#{String(d.sequence_number).padStart(2, "0")}</span>
        <span className={`truncate text-sm font-semibold ${hw ? "text-[#E5B842]" : "text-slate-200"} md:text-right`}>{d.home?.player?.display_name || "—"} <span className="font-mono text-slate-500 text-xs">{d.home?.total_odds || ""}</span></span>
        <span className="hidden md:inline text-slate-600 font-heading font-black text-xs px-1">VS</span>
        <span className={`truncate text-sm font-semibold ${aw ? "text-[#E5B842]" : "text-slate-200"}`}>{d.away?.player?.display_name || "—"} <span className="font-mono text-slate-500 text-xs">{d.away?.total_odds || ""}</span></span>
        <span className="justify-self-start md:justify-self-end col-span-3 md:col-span-1"><Outcome d={d} /></span>
        <span className="justify-self-end text-slate-600">{open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</span>
      </button>
      {open && (
        <div className="grid md:grid-cols-2 gap-3 px-4 pb-4 pt-1 border-t border-[#2b2512]/60" data-testid={`duel-expand-${d.sequence_number}`}>
          <CouponPanel coupon={d.home} club={home} win={hw} />
          <CouponPanel coupon={d.away} club={away} win={aw} />
        </div>
      )}
    </div>
  );
}

const SEL_STYLE = { WON: "text-emerald-400 border-emerald-500/40", LOST: "text-rose-400 border-rose-500/40", VOID: "text-slate-400 border-slate-600", PENDING: "text-amber-400 border-amber-500/40" };

function CouponPanel({ coupon, club, win }) {
  if (!coupon) return <div className="rounded-lg border border-dashed border-[#2b2512] p-3 text-center text-slate-600 text-xs font-mono uppercase">No coupon</div>;
  if (!coupon.content_visible) {
    return (
      <div className="rounded-lg border border-[#2b2512] bg-[#070707] p-3">
        <div className="text-[11px] font-mono uppercase text-slate-500">Sealed · blind until paired</div>
        <div className="text-sm text-slate-300 mt-1 truncate">{coupon.player?.display_name}</div>
      </div>
    );
  }
  return (
    <div className={`rounded-lg border bg-[#0d0d0d] p-3 ${win ? "border-emerald-500/40" : "border-[#2b2512]"}`}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="min-w-0">
          <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">{club?.short_name} · {coupon.side}</div>
          <Link to={coupon.player?.wsbf_player_id ? `/player/${coupon.player.wsbf_player_id}` : "#"} className="text-sm font-semibold text-slate-100 hover:text-[#E5B842] truncate block" style={{ transition: "color 0.15s ease" }}>{coupon.player?.display_name}</Link>
        </div>
        <div className="text-right shrink-0">
          <div className="font-mono text-[#E5B842] font-bold text-sm">{coupon.total_odds}</div>
          <div className="text-[9px] font-mono text-slate-600 uppercase">total odds · +{coupon.potential_team_points} TP</div>
        </div>
      </div>
      <ul className="space-y-1">
        {coupon.selections?.map((s, i) => (
          <li key={i} className="text-[11px] text-slate-400 flex justify-between gap-2 items-center">
            <span className="truncate">{s.event_label} · {s.market_name} · {s.label}</span>
            <span className="flex items-center gap-1.5 shrink-0">
              {s.result_status && <span className={`px-1.5 py-0.5 rounded text-[9px] font-mono font-bold uppercase border ${SEL_STYLE[s.result_status] || SEL_STYLE.PENDING}`}>{s.result_status}</span>}
              <span className="font-mono text-slate-300">{s.odds}</span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
